import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Award, ArrowRight, Coins, Check } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/rewards").then((r) => setRewards(r.data.filter((x)=>x.active)));
    api.get("/me/history").then((r) => setHistory(r.data.slice(0, 5)));
  }, []);

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="dashboard-page">
      <div className="fade-up">
        <div className="label-eyebrow mb-3">Tableau de bord</div>
        <h1 className="font-display text-5xl lg:text-6xl mb-2">Bonjour {user.firstName}.</h1>
        <p className="text-[#a3a8a4]">Voici vos points et les récompenses qui vous attendent.</p>
      </div>

      <div className="bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)] p-10 mt-12 fade-up-delay-1">
        <div className="flex items-center gap-2 label-eyebrow mb-6"><Coins size={14}/> Solde de points</div>
        <div className="flex items-end gap-4">
          <div className="font-display text-7xl lg:text-8xl gold-text" data-testid="dashboard-points">{user.points}</div>
          <div className="text-[#a3a8a4] mb-3">points cumulés</div>
        </div>
      </div>

      <div className="mt-16 fade-up-delay-2">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2">Catalogue des récompenses</div>
            <h2 className="font-display text-3xl lg:text-4xl">Ce que vous pouvez gagner</h2>
          </div>
          <Link to="/recompenses" className="text-sm gold-text hover:underline flex items-center gap-2" data-testid="dashboard-see-all-rewards">Tout voir <ArrowRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rewards.length === 0 && <div className="col-span-full text-[#a3a8a4]">Aucune récompense disponible pour le moment.</div>}
          {rewards.map((r) => {
            const reachable = user.points >= r.points_cost;
            const missing = r.points_cost - user.points;
            const progress = Math.min(100, (user.points / r.points_cost) * 100);
            return (
              <div key={r.id} className={`p-8 transition-colors ${reachable ? "border border-[#d4af37]/40 bg-[#1a241b]/20" : "border border-[rgba(253,251,247,0.08)] bg-[#0a0e0b] hover:border-[#d4af37]/30"}`} data-testid={`dashboard-reward-${r.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <Award className="gold-text" size={28}/>
                  {reachable && <span className="text-xs label-eyebrow inline-flex items-center gap-1"><Check size={12}/> Disponible</span>}
                </div>
                <div className="font-display text-2xl mb-2">{r.name}</div>
                <p className="text-sm text-[#a3a8a4] mb-6 min-h-[3rem] line-clamp-2">{r.description}</p>
                <div className="mb-3 flex justify-between items-baseline">
                  <span className="gold-text font-medium">{r.points_cost} pts</span>
                  {!reachable && <span className="text-xs text-[#a3a8a4]">Encore {missing} pts</span>}
                </div>
                <div className="h-1 bg-[#1a241b] overflow-hidden">
                  <div className="h-full bg-[#d4af37] transition-all" style={{width: `${progress}%`}}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-16 fade-up-delay-3">
        <div className="label-eyebrow mb-2">Historique récent</div>
        <h2 className="font-display text-3xl mb-6">Vos derniers mouvements</h2>
        <div className="border border-[rgba(253,251,247,0.08)]">
          {history.length === 0 && <div className="p-8 text-[#a3a8a4]">Aucune activité pour l'instant.</div>}
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between border-b border-[rgba(253,251,247,0.05)] last:border-0 px-6 py-4 hover:bg-[#1a241b]/40">
              <div>
                <div className="text-sm">{h.reason}</div>
                <div className="text-xs text-[#a3a8a4]">{new Date(h.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <div className={h.delta >= 0 ? "gold-text" : "text-red-400"} data-testid={`history-delta-${h.id}`}>
                {h.delta >= 0 ? "+" : ""}{h.delta} pts
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
